import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import Layout from "../layout/Layout";
import { apiV2 } from "../../../api/axiosConfig";
import { BlueButton } from "../../../components/ui/Buttons";
import Modal from "../../../components/portal/Modal";
import { Action, ActionSelect } from "../../../components/ui/ActionSelect";
import { HeaderComponentProps, PaginatedSelector } from "../../../components/paginated-selector/paginated-selector";
import { convertV1ToV2, PaginatedResponse } from "../../../types/PaginatedResponse";
import { ClientFilter } from "../../../components/paginated-selector/filters/ClientFilter";
import { FilterQueryParams } from "../../../components/paginated-selector/filters/types";
import { useAuth } from "../../../context/user";
import {useCopyID} from "@/lib/misc/useCopyID";

type NotificationGroup = {
    id: string;
    clientID: string;
    clientName: string;
    departmentID: string;
    departmentName: string;
    name: string;
    userIDs: string[];
};

const LIMIT = 20;

export const NotificationGroupList: React.FC = () => {
    const navigate = useNavigate();
    const { isSuperAdmin, isClientAdmin, isDepartmentAdmin } = useAuth();
    const {copyID, isCopied} = useCopyID();

    const [error, setError] = useState("");
    const [deleteTarget, setDeleteTarget] = useState<NotificationGroup | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const getGroups = useCallback(async (search: string, page: number, filterParams?: FilterQueryParams) => {
        const params: Record<string, string | number> = { page, limit: LIMIT, ...filterParams };
        if (search) params.search = search;
        const res = await apiV2.get<PaginatedResponse<NotificationGroup>>("notification-groups", { params });
        return convertV1ToV2(res.data, LIMIT);
    }, [refreshKey]); // eslint-disable-line

    const getColumns = useCallback((group: NotificationGroup) => {
        if (isSuperAdmin) {
            return [group.name, group.clientName ?? "", group.departmentName ?? "", String(group.userIDs?.length ?? 0)];
        }
        if (isClientAdmin || isDepartmentAdmin) {
            return [group.name, group.departmentName ?? "", String(group.userIDs?.length ?? 0)];
        }
        return [group.name, String(group.userIDs?.length ?? 0)];
    }, [isSuperAdmin, isClientAdmin, isDepartmentAdmin]);

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        if (isSuperAdmin && props.columnIndex === 1) {
            return <ClientFilter {...props} />;
        }
        return props.label;
    }, [isSuperAdmin]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await apiV2.delete(`notification-groups/${deleteTarget.id}`);
            setDeleteTarget(null);
            setRefreshKey((k) => k + 1);
        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else {
                setError("An error has occurred");
            }
        }
        setDeleting(false);
    };

    const headers = isSuperAdmin
        ? ["Name", "Client", "Department", "Users"]
        : (isClientAdmin || isDepartmentAdmin)
            ? ["Name", "Department", "Users"]
            : ["Name", "Users"];

    return (
        <Layout active="notification-groups">
            <div className="flex flex-col gap-8">
                {error && (
                    <div className="text-white bg-red rounded-md shadow-md p-4">{error}</div>
                )}

                <Modal open={!!deleteTarget} setOpen={(v: boolean) => { if (!v) setDeleteTarget(null); }}>
                    <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
                        <p className="font-semibold">Delete Notification Group</p>
                        <p>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</p>
                        <BlueButton text="Delete" loading={deleting} onClick={handleDelete} />
                    </div>
                </Modal>

                <div className="bg-white shadow-md p-4 flex flex-row items-center">
                    <span className="font-semibold">Notification Groups</span>
                    <div className="flex-1" />
                    <div className="w-32">
                        <BlueButton text="Add Group" onClick={() => navigate("/management/notification-groups/create")} />
                    </div>
                </div>

                <PaginatedSelector
                    headers={headers}
                    getItems={getGroups}
                    getColumns={getColumns}
                    disableSelect
                    renderHeader={renderHeader}
                    onColumnClick={(group, columnIndex) => {
                        if (columnIndex === 0) navigate(`/management/notification-groups/edit/${group.id}`);
                    }}
                    nonClickableColumns={headers.map((_, i) => i).filter(i => i !== 0)}
                    renderActions={(group) => (
                        <ActionSelect label="Select Action">
                            <Action label="Edit" onSelect={() => navigate(`/management/notification-groups/edit/${group.id}`)} />
                            <Action label="Delete" onSelect={() => setDeleteTarget(group)} />
                            <Action label={isCopied(group.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(group.id)} keepOpen />
                        </ActionSelect>
                    )}
                />
            </div>
        </Layout>
    );
};

NotificationGroupList.displayName = "NotificationGroupList";

export default NotificationGroupList;
